import {
    authApi,
    type ApiResponse,
    type GenericErrorResponse,
} from "./authService";

interface SendInvitationsErrorResponse extends GenericErrorResponse {
  invalid_emails?: string[];
}

interface InvitationResult {
  email: string;
  code: string;
  status: "sent" | "exists" | "error";
  message?: string;
  expires_at?: string;
}

export interface Invitation {
  code: string;
  email: string;
  created_at: string;
  expires_at: string;
  used_at: string | null;
  is_used: boolean;
  is_valid: boolean;
  is_expired: boolean;
  role?: string;
}

interface SendInvitationsResponse extends ApiResponse {
  results: InvitationResult[];
}

interface ResendInvitationResponse extends ApiResponse {
  result: InvitationResult;
}

interface MyInvitationsResponse extends ApiResponse {
  data: Invitation[];
}

export class InvitationService {
  static async sendInvitations(
    emails: string[]
  ): Promise<SendInvitationsResponse> {
    const response = (await authApi<SendInvitationsResponse>(
      "/invitations/send",
      {
        method: "POST",
        body: JSON.stringify({ emails }),
      }
    )) as SendInvitationsResponse | SendInvitationsErrorResponse;

    if (!response.success) {
      const errorResponse = response as SendInvitationsErrorResponse;
      const error = new Error(errorResponse.error) as Error & {
        invalid_emails?: string[];
      };
      if (errorResponse.invalid_emails) {
        error.invalid_emails = errorResponse.invalid_emails;
      }
      throw error;
    }

    return response;
  }

  static async sendAdminInvitations(
    emails: string[],
    labId?: string
  ): Promise<SendInvitationsResponse> {
    const response = (await authApi<SendInvitationsResponse>(
      "/invitations/send/user",
      {
        method: "POST",
        body: JSON.stringify({ emails, role: "admin", lab_id: labId }),
      }
    )) as SendInvitationsResponse | SendInvitationsErrorResponse;

    if (!response.success) {
      const errorResponse = response as SendInvitationsErrorResponse;
      const error = new Error(errorResponse.error) as Error & {
        invalid_emails?: string[];
      };
      if (errorResponse.invalid_emails) {
        error.invalid_emails = errorResponse.invalid_emails;
      }
      throw error;
    }

    return response;
  }

  static async sendUserInvitations(
    emails: string[],
    labId?: string
  ): Promise<SendInvitationsResponse> {
    const response = (await authApi<SendInvitationsResponse>(
      "/invitations/send/user",
      {
        method: "POST",
        body: JSON.stringify({ emails, role: "user", lab_id: labId }),
      }
    )) as SendInvitationsResponse | SendInvitationsErrorResponse;

    if (!response.success) {
      const errorResponse = response as SendInvitationsErrorResponse;
      const error = new Error(errorResponse.error) as Error & {
        invalid_emails?: string[];
      };
      if (errorResponse.invalid_emails) {
        error.invalid_emails = errorResponse.invalid_emails;
      }
      throw error;
    }

    return response;
  }

  static async sendUserInvitationForCertification(
    email: string,
    certificationId: number
  ): Promise<SendInvitationsResponse> {
    const response = (await authApi<SendInvitationsResponse>(
      "/invitations/send/user",
      {
        method: "POST",
        body: JSON.stringify({
          emails: [email],
          role: "user",
          certification_id: certificationId,
        }),
      }
    )) as SendInvitationsResponse | SendInvitationsErrorResponse;

    if (!response.success) {
      const errorResponse = response as SendInvitationsErrorResponse;
      const error = new Error(errorResponse.error) as Error & {
        invalid_emails?: string[];
      };
      if (errorResponse.invalid_emails) {
        error.invalid_emails = errorResponse.invalid_emails;
      }
      throw error;
    }

    return response;
  }

  static async resendInvitation(
    email: string
  ): Promise<ResendInvitationResponse> {
    const response = (await authApi<ResendInvitationResponse>(
      "/invitations/resend",
      {
        method: "POST",
        body: JSON.stringify({ email }),
      }
    )) as ResendInvitationResponse | GenericErrorResponse;

    if (!response.success) {
      throw new Error(response.error);
    }

    return response;
  }

  static async validateInvitation(code: string): Promise<ApiResponse> {
    const response = (await authApi("/invitations/validate", {
      method: "POST",
      body: JSON.stringify({ code }),
    })) as ApiResponse | GenericErrorResponse;

    if (!response.success) {
      throw new Error(response.error);
    }

    return response;
  }

  static async getMyInvitations(): Promise<MyInvitationsResponse> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await authApi<any>("/invitations/my-invitations");

    if (!response.success) {
      throw new Error(response.error || "Failed to fetch invitations");
    }

    // authApi spreads the array as { success: true, 0: {...}, 1: {...} }
    // Extract numeric keys into an array
    const invitations: Invitation[] = [];
    let index = 0;
    while (response[index] !== undefined) {
      invitations.push(response[index] as Invitation);
      index++;
    }

    return {
      success: true,
      message: "Invitations fetched successfully",
      data: invitations,
    };
  }
}
